import { http } from "../../lib/http";

/* =========================================================================
   SUPERFICIE DE API
   Un único lugar donde vive cada llamada al backend. Las pantallas no arman
   URLs: si una ruta cambia en Laravel, se corrige acá y nada más.

   Dos familias:
   · `/admin/*`  — endpoints de superadministración (bloque nuevo en
                   routes/api.php, protegido por rol 4).
   · el resto    — endpoints que ya existían y sirven igual para el panel.
   ======================================================================== */

/* ------------------------------- SESIÓN ------------------------------- */
export const auth = {
  /**
   * `two_factor_code` viaja solo cuando el servidor lo pide.
   *
   * El primer intento va sin él; si la cuenta tiene segundo factor, responde 401
   * con `reason: two_factor_required` y la pantalla vuelve a llamar con el
   * código. Mandarlo siempre obligaría a pedirlo a todo el mundo, incluso a
   * quien no lo tiene activo.
   */
  login: (email, password, two_factor_code) =>
    http
      .post("/login", { email, password, two_factor_code })
      .then((r) => r.data),
  profile: () => http.get("/profile").then((r) => r.data),
  logout: () => http.post("/logout").then((r) => r.data),
};


/* ------------------------------- MEDIOS -------------------------------- */
/**
 * Archivos de cualquier entidad en R2. `entidad` es una de las raíces que
 * admite el backend: negocios, productos, usuarios, conjuntos.
 *
 * La subida va por el backend: las llaves del bucket dan permiso de escritura
 * sobre todo y no pueden llegar al navegador.
 */
/* ---------------------- SEGURIDAD DE LA CUENTA PROPIA ------------------ */
/**
 * Segundo factor y sesiones abiertas.
 *
 * Todo es sobre UNO MISMO: no hay forma de tocar el segundo factor de otra
 * persona ni de ver sus sesiones. Por eso no lleva `modulo:` en el servidor y no
 * depende del área.
 */
export const seguridad = {
  estado: () => http.get("/admin/security").then((r) => r.data),

  /** Paso 1: genera el secreto y devuelve la dirección para el código QR. */
  iniciar2FA: () => http.post("/admin/security/two-factor").then((r) => r.data),

  /** Paso 2: confirma con el código del teléfono y devuelve los de recuperación. */
  confirmar2FA: (code) =>
    http
      .post("/admin/security/two-factor/confirm", { code })
      .then((r) => r.data),

  /** Quitarlo exige la contraseña: una sesión abierta no puede bastar. */
  desactivar2FA: (password) =>
    http
      .delete("/admin/security/two-factor", { data: { password } })
      .then((r) => r.data),

  regenerarCodigos: () =>
    http.post("/admin/security/two-factor/recovery-codes").then((r) => r.data),

  cerrarSesion: (id) =>
    http.delete(`/admin/security/sessions/${id}`).then((r) => r.data),

  cerrarLasDemas: () =>
    http.delete("/admin/security/sessions/others").then((r) => r.data),
};


/* ------------------------------ USUARIOS ------------------------------ */
export const users = {
  list: (params) => http.get("/admin/users", { params }).then((r) => r.data),
  create: (body) => http.post("/admin/users", body).then((r) => r.data),
  show: (id) => http.get(`/admin/users/${id}`).then((r) => r.data),
  update: (id, body) =>
    http.put(`/admin/users/${id}`, body).then((r) => r.data),
  setState: (id, state) =>
    http.put(`/admin/users/${id}`, { state }).then((r) => r.data),
  setRole: (id, rol) =>
    http.put(`/admin/users/${id}`, { rol }).then((r) => r.data),

  /*
   * DA POR BUENO UN CORREO SIN QUE SU DUEÑO ABRA EL ENLACE.
   *
   * Verbo propio y no un campo más de `update`: no es editar un dato, es
   * afirmar algo que nadie comprobó. El servidor guarda quién lo hizo, para
   * que una verificación de verdad y una afirmada a mano sigan siendo
   * distinguibles.
   */
  verifyEmail: (id) =>
    http.post(`/admin/users/${id}/verify-email`).then((r) => r.data),
};


/* --------------------------- ÁREAS Y ACCESOS --------------------------- */
/**
 * El área a la que pertenece cada persona decide qué secciones ve.
 *
 * `misPermisos` se pide aparte de `/profile` porque el menú tiene que poder
 * refrescarse cuando Tecnología cambia un reparto, sin obligar a recargar la
 * sesión entera.
 */
export const areas = {
  misPermisos: () => http.get("/admin/me/permissions").then((r) => r.data),

  list: () => http.get("/admin/areas").then((r) => r.data),
  create: (body) => http.post("/admin/areas", body).then((r) => r.data),
  update: (id, body) =>
    http.put(`/admin/areas/${id}`, body).then((r) => r.data),
  remove: (id) => http.delete(`/admin/areas/${id}`).then((r) => r.data),

  /** Personal de la empresa (rol 4) con su área y su nivel. */
  members: () => http.get("/admin/areas-members").then((r) => r.data),
  setArea: (userId, area_id) =>
    http.put(`/admin/areas-members/${userId}`, { area_id }).then((r) => r.data),
  /**
   * Nivel dentro del área: 'gestor' o 'consulta'.
   *
   * El área define QUÉ SECCIONES ve alguien; el nivel, si además puede
   * modificarlas. Es lo que permite tener un auxiliar y su jefe en la misma
   * área sin duplicarla.
   */
  setLevel: (userId, access_level) =>
    http
      .put(`/admin/areas-members/${userId}`, { access_level })
      .then((r) => r.data),
};


/* ------------------------- AJUSTES DE LA PLATAFORMA ------------------- */
/**
 * Reglas de la operación y control de la app móvil.
 *
 * `index` trae el CATÁLOGO además de los valores: la pantalla no sabe qué
 * ajustes existen ni de qué tipo es cada uno, se lo dice el servidor. Así
 * agregar un ajuste no obliga a tocar el panel.
 */
export const settings = {
  index: () => http.get("/admin/settings").then((r) => r.data),

  /**
   * El cuerpo va ANIDADO por grupo —`{operacion: {horas_estancado: 12}}`—
   * porque para Laravel un punto en el nombre de un campo significa "dentro de
   * este arreglo": con las claves punteadas planas, las reglas no encontraban
   * nada que validar y la petición contestaba 200 sin guardar ni rechazar.
   */
  update: (anidado) => http.put("/admin/settings", anidado).then((r) => r.data),

  restablecer: (key) =>
    http.put("/admin/settings/restablecer", { key }).then((r) => r.data),
};
